const CDN_URL = 'https://cdn.lyricsweb.com';
const OLD_DOMAIN = 'https://lyricsweb.com';

/**
 * Transform image URL to use CDN
 * Handles:
 * - /uploads/... -> https://cdn.lyricsweb.com/uploads/...
 * - https://lyricsweb.com/uploads/... -> https://cdn.lyricsweb.com/uploads/...
 * - Already CDN URLs are returned as-is
 * - External URLs are returned as-is
 */
export function toCdnUrl(url) {
  if (!url) return null;

  // Already a CDN URL
  if (url.startsWith(CDN_URL)) {
    return url;
  }

  // External URL (not our domain) - return as-is
  if (url.startsWith('http') && !url.includes('lyricsweb.com') && !url.startsWith('/uploads')) {
    return url;
  }

  // Old domain URL: https://lyricsweb.com/uploads/... -> CDN
  if (url.startsWith(OLD_DOMAIN + '/uploads')) {
    return url.replace(OLD_DOMAIN, CDN_URL);
  }

  // Relative path: /uploads/... -> CDN
  if (url.startsWith('/uploads/')) {
    return `${CDN_URL}${url}`;
  }

  // Path without leading slash: uploads/... -> CDN
  if (url.startsWith('uploads/')) {
    return `${CDN_URL}/${url}`;
  }

  return url;
}

export function artistImageUrl(artist){
  const url = artist?.artist_image_link || artist?.downloaded_image_link || "/default-image.jpg";
  return toCdnUrl(url) || "/default-image.jpg";
}

export function albumImageUrl(album){
  const url = album?.image_link ||
    album?.album_image_link ||
    album?.downloaded_image_link ||
    "/default-image.jpg";
  return toCdnUrl(url) || "/default-image.jpg";
}

export function songImageUrl(song){
  let url;
  if(song?.song){
    url = song?.song?.song_image_downloaded_link || "/default-image.jpg";
  } else {
    url = song?.song_image_link ||
      song?.downloaded_image_link ||
      song?.image_link ||
      song?.song_image_downloaded_link ||
      "/default-image.jpg";
  }
  return toCdnUrl(url) || "/default-image.jpg";
}

export function newsImageUrl(news) {
  const url = news?.coverImg || news?.coverImage || "/default-image.jpg";
  return toCdnUrl(url) || "/default-image.jpg";
}

export function authorImageUrl(author) {
  const url = author?.authorPic || author?.avatar || "/default-image.jpg";
  return toCdnUrl(url) || "/default-image.jpg";
}

/**
 * Extract artist name from an album object with multiple fallback fields.
 * Different API endpoints return artist data in different shapes, so this
 * utility tries all known field paths before falling back to "Unknown Artist".
 */
export function albumArtistName(album, fallback = "Unknown Artist") {
  if (!album) return fallback;
  return (
    album.artist_details?.artist_name ||
    album.artist?.artist_name ||
    album.artist_name ||
    (typeof album.artists === "string" ? album.artists : null) ||
    (Array.isArray(album.artists) && album.artists.length > 0
      ? album.artists.map((a) => (typeof a === "string" ? a : a?.name || a?.artist_name)).filter(Boolean).join(", ")
      : null) ||
    fallback
  );
}

/**
 * Format a numeric count for display.
 *  - null/undefined → null (so components can conditionally hide)
 *  - 1500000 → "1.5M"
 *  - 23400   → "23.4K"
 *  - 999     → "999"
 */
export function formatViewCount(count) {
  if (count == null || count === "" || isNaN(Number(count))) return null;
  const n = Number(count);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString();
}

export function decodeMojibake(value) {
  if (!value || typeof value !== "string") return value;
  // Heuristic: common UTF-8 -> Latin-1 mojibake markers
  const suspicious = /[\u00C2\u00C3\u00E0\u00E2\u00EF\u00FFFD]/;
  if (!suspicious.test(value)) return value;
  try {
    const bytes = Uint8Array.from(value, (c) => c.charCodeAt(0));
    const decoded = new TextDecoder("utf-8").decode(bytes);
    if (decoded && decoded !== value) return decoded;
  } catch {
    // Ignore and fall back to other strategy
  }
  try {
    const decoded = decodeURIComponent(escape(value));
    if (decoded && decoded !== value) return decoded;
  } catch {
    // Ignore if decoding fails
  }
  return value;
}

export function htmlToText(value) {
  if (!value || typeof value !== "string") return value;
  if (!/[<>]/.test(value)) return value;

  const withBreaks = value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n");

  if (typeof window !== "undefined" && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(withBreaks, "text/html");
    const text = doc.body.textContent || "";
    return text.replace(/\n{3,}/g, "\n\n").trim();
  }

  return withBreaks.replace(/<[^>]*>/g, "").replace(/\n{3,}/g, "\n\n").trim();
}

export function normalizeTranslationLabel(value) {
  if (!value || typeof value !== "string") return value;
  return value
    .replace(/\(اردو ترجمہ\)/g, "(Urdu translation)")
    .replace(/\(اردو ترجمعہ\)/g, "(Urdu translation)")
    .replace(/\(Український переклад\)/g, "(Ukrainian translation)");
}


export function extractObjectId(id){
  if (!id) return '';
  if (typeof id === 'string') return id;
  if (id.$oid) return id.$oid; // Extended JSON format
  // Handle MongoDB Binary/Buffer format: { buffer: { 0: 105, 1: 8, ... } }
  if (id.buffer && typeof id.buffer === 'object') {
    const bytes = id.buffer;
    let hex = '';
    for (let i = 0; i < 12; i++) {
      if (bytes[i] !== undefined) {
        hex += bytes[i].toString(16).padStart(2, '0');
      }
    }
    if (hex.length === 24) return hex;
  }
  if (typeof id.toString === 'function') {
    const str = id.toString();
    // Check if toString returned a valid hex string (24 chars) not "[object Object]"
    if (/^[a-f0-9]{24}$/i.test(str)) return str;
  }
  return '';
}

/**
 * Safely format a date value
 * Handles MongoDB date objects, ISO strings, and invalid dates
 * @param {string|Date|object} dateValue - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @param {string} fallback - Fallback text for invalid dates
 * @returns {string}
 */
export function formatDate(dateValue, options = {}, fallback = 'N/A') {
  if (!dateValue) return fallback;

  // Handle MongoDB extended JSON format { $date: "..." }
  if (dateValue.$date) {
    dateValue = dateValue.$date;
  }

  const date = new Date(dateValue);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return fallback;
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  return date.toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format date with time
 */
export function formatDateTime(dateValue, fallback = 'N/A') {
  if (!dateValue) return fallback;

  if (dateValue.$date) {
    dateValue = dateValue.$date;
  }

  const date = new Date(dateValue);

  if (isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
